import { createContext } from 'react';

export interface ToolCallSummaryField {
  label: string;
  value: string;
}

export interface ToolCallInfo {
  name: string;
  label: string;
  arguments: Record<string, unknown>;
  summary?: Array<ToolCallSummaryField> | null;
}

export type ChatThreadMode = 'default' | 'fraud';

export interface ThreadContextSnapshot {
  customerId: string | null;
  customerName: string | null;
  orderId: string | null;
  department: string | null;
  mode: ChatThreadMode;
  page: string | null;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'context';
  content: string;
  toolCalls?: Array<ToolCallInfo>;
  responseId?: string;
  createdAt: number;
  contextSnapshot?: ThreadContextSnapshot | null;
}

export interface ChatThread {
  id: string;
  openaiConversationId: string;
  conversationRef: string;
  title: string;
  mode: ChatThreadMode;
  messages: Array<ChatMessage>;
  latestContextSnapshot: ThreadContextSnapshot | null;
  createdAt: number;
  updatedAt: number;
}

export interface AiChatState {
  isOpen: boolean;
  threads: Record<string, ChatThread>;
  activeThreadId: string | null;
  activeThread: ChatThread | null;
  sendingThreadIds: Array<string>;
  errorsByThreadId: Record<string, string | null>;
  globalError: string | null;
  currentUrlCustomerId: string | null;
  currentUrlOrderId: string | null;
  currentContextSnapshot: ThreadContextSnapshot | null;
}

export interface AiChatActions {
  openChat: () => Promise<void>;
  closeChat: () => void;
  createThread: (
    mode?: ChatThreadMode,
    initialContextSnapshot?: ThreadContextSnapshot | null,
  ) => Promise<string | null>;
  sendMessage: (threadId: string, content: string, contextSnapshot: ThreadContextSnapshot | null) => Promise<void>;
  switchThread: (threadId: string) => void;
  deleteThread: (threadId: string) => void;
}

export type AiChatContextType = AiChatState & AiChatActions;

export const AiChatContext = createContext<AiChatContextType | null>(null);
